import { useEffect, useRef } from 'react';
import { IonContent, IonFooter, IonToolbar, IonButton } from '@ionic/react';
import '../../../theme/FlowModal.css';

export const STEP_TITLE_ID = 'flow-step-title';
const STEP_HEADING_ATTR = 'data-flow-step-heading';

/** Move keyboard and screen-reader focus to the current step's heading. */
export function focusStepHeading(root) {
    const heading = (root || document).querySelector(`[${STEP_HEADING_ATTR}]`);
    if (heading) {
        heading.focus({ preventScroll: true });
    }
}

/**
 * One step of a modal flow: a scrolling body with a focusable heading, and a
 * sticky footer holding the primary action. Pass `hint` to explain why the
 * action is disabled; omit `primaryLabel` for a step with no action.
 */
function StepLayout({
    title,
    children,
    primaryLabel,
    onPrimary,
    primaryDisabled = false,
    hint,
}) {
    const headingRef = useRef(null);

    // Each step mounts fresh when the page changes, so focusing on mount
    // announces the new step to assistive technology.
    useEffect(() => {
        const id = window.setTimeout(() => {
            if (headingRef.current) {
                headingRef.current.focus({ preventScroll: true });
            }
        }, 0);
        return () => window.clearTimeout(id);
    }, []);

    return (
        <>
            <IonContent className='flow-step'>
                <div className='flow-step__body'>
                    <h2
                        id={STEP_TITLE_ID}
                        ref={headingRef}
                        tabIndex={-1}
                        className='flow-step__title'
                        data-flow-step-heading=''
                    >
                        {title}
                    </h2>
                    {children}
                </div>
            </IonContent>
            {primaryLabel && (
                <IonFooter className='flow-footer'>
                    <IonToolbar>
                        <div className='flow-footer__inner'>
                            {hint && (
                                <p className='flow-footer__hint' role='status'>
                                    {hint}
                                </p>
                            )}
                            <IonButton
                                className='flow-primary'
                                expand='block'
                                shape='round'
                                disabled={primaryDisabled}
                                onClick={onPrimary}
                            >
                                {primaryLabel}
                            </IonButton>
                        </div>
                    </IonToolbar>
                </IonFooter>
            )}
        </>
    );
}

export default StepLayout;
